import {
  PokemonType,
  SkillData,
  SkillEvent_Global,
  SkillEvent_Vfx,
  SkillEventCheck,
  SkillMomentum,
  SkillRangeType,
  SkillTeamCondition,
  SkillType,
  StatusEffect,
  TeamCondition,
  TeamConditionEffect,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { msToTicks } from "../utils/ms-to-ticks";

export const [
  MAGMA_STORM,
  WATER_SPOUT,
  OVERGROWTH,
  ELECTRIC_WRATH,
  BLIZZARD,
  METEOR_ASSAULT,
  ACID_RAIN,
  FISSURE,
  SKY_ATTACK,
  FUTURE_SIGHT,
  SIGNAL_SPAM,
  STONE_CRUSH,
  POLTERGEIST,
  GREAT_ROAR,
  NIGHT_DAZE,
  METAL_BURST,
  MOONBLAST,
  SANDSTORM,
]: SkillData[] = [
  {
    id: "magma-storm",
    description: "Unleashes a firestorm that damages all enemies.",
    power: 20,
    pokemon_type: PokemonType.FIRE,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/fire-4.png",
        y_offset: -55,
        sprite_scale: 2,
        loops: 1,
        speed: 1,
        duration_ticks: 20,
      },
      {
        sprite_path: "/assets/spells/tile-fx/fire-4.png",
        y_offset: -55,
        sprite_scale: 2,
        loops: 1,
        speed: 1,
        duration_ticks: 20,
      },
      {
        sprite_path: "/assets/spells/tile-fx/fire-4.png",
        y_offset: -55,
        sprite_scale: 2,
        loops: 1,
        speed: 0.3,
      },
    ],
  },
  {
    id: "water-spout",
    description:
      "Douses all enemies with water. Burns if a Fire-type ally is present.",
    power: 10,
    pokemon_type: PokemonType.WATER,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/water-1.png",
        sprite_scale: 0.8,
        loops: 1,
        speed: 0.7,
      },
    ],
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.FIRE,
          value: 1,
        },
        effect: {
          stat: "apply_status",
          value: 1,
          status_effects: [
            {
              effect: {
                status: StatusEffect.BURN,
                duration_ticks: msToTicks(2000),
                intensity: 2,
              },
              target: "all_enemies",
            },
          ],
        } as TeamConditionEffect,
      },
    ] as SkillData["team_conditions"],
  },
  {
    id: "overgrowth",
    description: "Summons vines to damage all enemies.",
    power: 10,
    pokemon_type: PokemonType.GRASS,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/vine_whip_upwards_000__12.png",
        sprite_scale: 1.25,
        loops: 1,
        speed: 0.6,
        damage: 0,
        duration_ticks: 20,
        blocks_event_pipeline: true,
      },

      {
        sprite_path: "/assets/spells/impact/fantasy-green-1.png",
        sprite_scale: 2,
        loops: 1,
        speed: 0.25,
        damage: 20,
        duration_ticks: 10,
      },
    ],
    event_checks: [
      {
        __typename: "SkillEventCheck",
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.SPEED_DOWN,
          },
        ],
        effects: [
          {
            type: "DAMAGE_MULTIPLIER",
            value: 1.25,
          },
        ],
      } as SkillEventCheck,
    ],
  },
  {
    id: "electric-wrath",
    description: "Strikes all enemies with divine electricity.",
    power: 15,
    pokemon_type: PokemonType.ELECTRIC,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/electric-1.png",
        sprite_scale: 0.8,
        loops: 1,
        speed: 0.7,
      },
    ],
    event_checks: [
      {
        __typename: "SkillEventCheck",
        conditions: [
          {
            type: "CASTER_HP_LT",
            value: 0.5,
          },
        ],
        effects: [
          {
            type: "DAMAGE_MULTIPLIER",
            value: 1.5,
          },
        ],
      } as SkillEventCheck,
    ],
  },
  {
    id: "blizzard",
    description:
      "Summons a massive blizzard to damage all enemies. Reduces 10% cooldown per use, up to 50% reduction.",
    power: 10,
    pokemon_type: PokemonType.ICE,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/ice-2.png",
        sprite_scale: 1.2,
        loops: 1,
        speed: 0.2,
        damage: 0,
      },
      {
        sprite_path: "/assets/spells/tile-fx/magic-puff-blue.png",
        sprite_scale: 2,
        loops: 1,
        speed: 0.37,
        damage: 20,
      },
    ],
    momentum: {
      max_stacks: 5,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
  },
  {
    id: "meteor-assault",
    description: "Strikes all enemies with falling meteors.",
    power: 10,
    pokemon_type: PokemonType.FIGHTING,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/red_punch_000__13.png",
        sprite_scale: 2,
        loops: 1,
        speed: 0.5,
        damage: 0,
        duration_ticks: 10,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/tile-fx/red_punch_000__13.png",
        sprite_scale: 0.04,
        loops: 1,
        speed: 0.5,
        damage: 12,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/tile-fx/red_punch_000__13.png",
        sprite_scale: 0.04,
        loops: 1,
        speed: 0.5,
        damage: 12,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/tile-fx/red_punch_000__13.png",
        sprite_scale: 0.04,
        loops: 1,
        speed: 0.5,
        damage: 12,
        duration_ticks: 10,
      },
    ],
  },
  {
    id: "acid-rain",
    description: "Summons acidic rain that damages all enemies.",
    power: 10,
    pokemon_type: PokemonType.POISON,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/poison-4.png",
        sprite_scale: 0.75,
        loops: 1,
        speed: 0.7,
        damage: 30,
      },
    ],
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.FIRE,
          value: 1,
        },
        effect: {
          stat: "apply_status",
          value: 1,
          status_effects: [
            {
              effect: {
                status: StatusEffect.BURN,
                duration_ticks: msToTicks(1500),
                intensity: 4,
              },
              target: "all_enemies",
            },
          ],
        } as TeamConditionEffect,
      },
    ] as SkillData["team_conditions"],
  },
  {
    id: "fissure",
    description: "Opens the ground to damage all enemies.",
    power: 10,
    pokemon_type: PokemonType.GROUND,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/ground-4.png",
        sprite_scale: 0.8,
        loops: 2,
        speed: 1,
        damage: 30,
      },
    ],
  },
  {
    id: "sky-attack",
    description: "Strikes all enemies with a powerful aerial blow.",
    power: 10,
    pokemon_type: PokemonType.FLYING,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/flying-1b.png",
        sprite_scale: 1.5,
        loops: 1,
        speed: 0.35,
        damage: 0,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/tile-fx/flying-1b.png",
        sprite_scale: 0.05,
        loops: 1,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/tile-fx/flying-1b.png",
        sprite_scale: 0.05,
        loops: 1,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/tile-fx/flying-1b.png",
        sprite_scale: 0.05,
        loops: 1,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/tile-fx/flying-1b.png",
        sprite_scale: 0.05,
        loops: 1,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/tile-fx/flying-1b.png",
        sprite_scale: 0.05,
        loops: 1,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
      },
    ],
  },
  {
    id: "future-sight",
    description: "Strikes all enemies with psychic energy.",
    power: 10,
    pokemon_type: PokemonType.PSYCHIC,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/psychic-2.png",
        sprite_scale: 2,
        loops: 1,
        speed: 0.37,
        damage: 0,
      },
      {
        sprite_path: "/assets/spells/tile-fx/psychic-4.png",
        sprite_scale: 1,
        loops: 3,
        speed: 1,
        damage: 0,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/impact/fantasy-pink-1.png",
        sprite_scale: 0.1,
        loops: 2,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/impact/fantasy-pink-1.png",
        sprite_scale: 0.1,
        loops: 2,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
      },
      {
        sprite_path: "/assets/spells/impact/fantasy-pink-1.png",
        sprite_scale: 0.1,
        loops: 2,
        speed: 0.5,
        damage: 5,
        duration_ticks: 10,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/impact/fantasy-pink-1.png",
        sprite_scale: 2.5,
        loops: 2,
        speed: 0.5,
        damage: 15,
        duration_ticks: 10,
      },
    ],
    event_checks: [
      {
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.POISON,
          },
        ],
        effects: [
          {
            type: "APPLY_STATUS_TARGET",
            status_effects: [
              {
                status: StatusEffect.POISON,
                duration_ticks: msToTicks(1500),
                intensity: 4,
              },
            ],
          },
        ],
      },
      {
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.BURN,
          },
        ],
        effects: [
          {
            type: "APPLY_STATUS_TARGET",
            status_effects: [
              {
                status: StatusEffect.BURN,
                duration_ticks: msToTicks(1500),
                intensity: 4,
              },
            ],
          },
        ],
      },
    ] as SkillData["event_checks"],
  },
  {
    id: "signal-spam",
    description: "Fires multiple bug signals at all enemies.",
    cooldown_ticks: msToTicks(5000),
    power: 15,
    pokemon_type: PokemonType.BUG,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/bug-1.png",
        sprite_scale: 1,
        loops: 1,
        speed: 0.7,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/vfx/leaves_012__23.png",
        sprite_scale: 0.5,
        loops: 1,
        speed: 0.6,
        damage: 0,
        duration_ticks: 10,
        blocks_event_pipeline: false,
      },
    ],
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.PSYCHIC,
          value: 1,
        },
        effect: {
          stat: "damage_mult",
          value: 1.4,
        } as TeamConditionEffect,
      },
    ] as SkillData["team_conditions"],
  },
  {
    id: "stone-crush",
    description: "Crushes all enemies with falling rocks.",
    power: 10,
    pokemon_type: PokemonType.ROCK,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/particle/rock-1.png",
        sprite_scale: 1.5,
        loops: 50,
        speed: 1,
        damage: 0,
        falling: true,
        y_offset: -150,
        duration_ticks: 25,
      },
      {
        sprite_path: "/assets/spells/tile-fx/rock_falling_stack_000__9.png",
        sprite_scale: 1.65,
        loops: 1,
        speed: 0.55,
        damage: 0,
        y_offset: -30,
        duration_ticks: 25,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/impact/ranged-ground-1.png",
        sprite_scale: 1,
        loops: 1,
        speed: 0.7,
        damage: 20,
      },
    ],
  },
  {
    id: "poltergeist",
    description: "Summons ghosts to haunt and damage all enemies.",
    power: 10,
    pokemon_type: PokemonType.GHOST,
    spellhit_vfx: [
      {
        damage: 0,
        sprite_path: "/assets/spells/vfx/fear_000__19.png",
        sprite_scale: 1.2,
        loops: 1,
        speed: 0.5,
        duration_ticks: 40,
        anchor_to_target: true,
        y_offset: -40,
      },
      {
        sprite_path: "/assets/spells/impact/fantasy-black-1.png",
        sprite_scale: 1.2,
        loops: 1,
        speed: 0.5,
        damage: 20,
        anchor_to_target: true,
      },
    ],
  },
  {
    id: "great-roar",
    description: "Damages all enemies. If last alive, causes fear.",
    power: 10,
    pokemon_type: PokemonType.DRAGON,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/great-roar.png",
        sprite_scale: 0.8,
        loops: 1,
        speed: 0.7,
        damage: 30,
      },
    ],
    team_conditions: [
      {
        condition: {
          type: "last_alive",
        } as TeamCondition,
        effect: {
          stat: "apply_status",
          value: 1,
          status_effects: [
            {
              effect: {
                status: StatusEffect.FEAR,
                duration_ticks: msToTicks(1500),
                intensity: 1,
              },
              target: "all_enemies",
            },
          ],
        } as TeamConditionEffect,
      },
    ],
  },
  {
    id: "night-daze",
    description:
      "Unleashes a dark shockwave that damages all enemies. 25% bonus damage if user has less than 25% HP left.",
    power: 10,
    pokemon_type: PokemonType.DARK,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/vfx/night-daze.png",
        sprite_scale: 1,
        loops: 1,
        speed: 0.3,
        anchor_to_target: true,
        y_offset: -50,
        damage: 0,
        duration_ticks: 20,
      },
      {
        sprite_path:
          "/assets/spells/impact/impfx1_toon_impact_B_large_white.png",
        sprite_scale: 1.25,
        loops: 1,
        speed: 0.35,
        damage: 20,
      },
    ],
    event_checks: [
      {
        conditions: [
          {
            type: "CASTER_HP_LT",
            value: 0.25,
          },
        ],
        effects: [
          {
            type: "DAMAGE_MULTIPLIER",
            value: 1.25,
          },
        ],
      },
    ] as SkillData["event_checks"],
  },
  {
    id: "metal-burst",
    description: "Explodes with metallic energy to damage all enemies.",
    power: 10,
    pokemon_type: PokemonType.STEEL,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/vfx/steel-bomb-blue.png",
        sprite_scale: 1.5,
        loops: 1,
        speed: 0.7,
        damage: 0,
        duration_ticks: 35,
        anchor_to_target: true,
        y_offset: -60,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/self/charge-1-white.png",
        sprite_scale: 2.5,
        loops: 1,
        speed: 0.35,
        damage: 0,
        duration_ticks: 20,
        anchor_to_target: true,
      },
      {
        sprite_path:
          "/assets/spells/impact/impfx2_fantasy_impact_A_large_orange.png",
        sprite_scale: 2,
        loops: 1,
        speed: 0.35,
        damage: 17,
        duration_ticks: 20,
      },
    ],
  },
  {
    id: "moonblast",
    description:
      "Blasts all enemies with powerful lunar energy. 25% bonus damage if there is a DARK-type ally on the team.",
    power: 10,
    pokemon_type: PokemonType.FAIRY,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/vfx/self_sparkles_000__48.png",
        sprite_scale: 1.8,
        loops: 1,
        speed: 1,
        damage: 0,
        duration_ticks: 40,
        anchor_to_target: true,
        blocks_event_pipeline: true,
      },
      {
        sprite_path: "/assets/spells/impact/fantasy-pink-1.png",
        sprite_scale: 2,
        loops: 1,
        speed: 0.2,
        damage: 20,
        duration_ticks: 40,
        anchor_to_target: true,
      },
    ],
    extra_charge_vfx: [
      {
        sprite_path: "/assets/spells/vfx/moonblast.png",
        sprite_scale: 1.2,
        loops: 1,
        speed: 0.2,
        anchor_to_target: true,
        y_offset: -60,
        blocks_event_pipeline: false,
      },
    ],
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.DARK,
          value: 1,
        },
        effect: {
          stat: "damage_mult",
          value: 1.25,
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ] as SkillData["team_conditions"],
  },
  {
    id: "sandstorm",
    description: "Summons a sandstorm that damages all enemies.",
    power: 10,
    pokemon_type: PokemonType.GROUND,
    spellhit_vfx: [
      {
        sprite_path: "/assets/spells/particle/rock-1.png",
        sprite_scale: 1,
        loops: 50,
        speed: 1,
        damage: 0,
        falling: true,
        y_offset: -150,
        duration_ticks: 15,
      },
      {
        sprite_path: "/assets/spells/tile-fx/ground-2.png",
        sprite_scale: 1,
        loops: 1,
        speed: 0.25,
        damage: 0,
        duration_ticks: 75,
        blocks_event_pipeline: false,
      },
      {
        sprite_path: "/assets/spells/impact/ranged-ground-1.png",
        sprite_scale: 1,
        loops: 1,
        speed: 0.7,
        damage: 17,
      },
    ],
  },
  //////
  //////
  //////
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description || "",
  power: skill.power,
  pokemon_type: skill.pokemon_type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: skill.cooldown_ticks ?? msToTicks(12000),
  mana_cost: 30,
  range: {
    type: SkillRangeType.ALL_ENEMIES,
    value: 0,
  },
  charging: {
    is_required: true,
    align_to: "south",
    duration_ticks: 60,
    anim_on_end: {
      name: "special",
      dir: "south",
    },
  } as const,
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 60,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/charge-1.png",
      sprite_scale: 2.5,
      speed: 0.2,
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    ...(skill.extra_charge_vfx ?? []).map(
      (vfx) =>
        ({
          __typename: "Vfx",
          duration_ticks: 60,
          target: {
            type: "self",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          sprite_path: vfx.sprite_path,
          sprite_scale: vfx.sprite_scale,
          loops: vfx.loops,
          speed: vfx.speed,
          damage: "damage" in vfx ? vfx.damage : 0,
          effect_delay_ticks: 0,
          blocks_event_pipeline:
            "blocks_event_pipeline" in vfx ? vfx.blocks_event_pipeline : true,
          anchor_to_target:
            "anchor_to_target" in vfx ? vfx.anchor_to_target : false,
          y_offset: "y_offset" in vfx ? vfx.y_offset : 0,
          falling: "falling" in vfx ? vfx.falling : false,
        }) as SkillEvent_Vfx,
    ),
    ...skill.spellhit_vfx.map(
      (vfx, index) =>
        ({
          __typename: "Global",
          duration_ticks: "duration_ticks" in vfx ? vfx.duration_ticks : 50,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          vfx: vfx,
          damage:
            "damage" in vfx
              ? vfx.damage
              : skill.power
                ? skill.power / skill.spellhit_vfx.length
                : 20,
          effect_delay_ticks: 0,
          blocks_event_pipeline:
            "blocks_event_pipeline" in vfx ? vfx.blocks_event_pipeline : true,
          anchor_to_target:
            "anchor_to_target" in vfx ? vfx.anchor_to_target : false,
          y_offset: "y_offset" in vfx ? vfx.y_offset : 0,
          falling: "falling" in vfx ? vfx.falling : false,
        }) as SkillEvent_Global,
    ),
  ],
}));
